/**
 * Chat Interface Component
 * VOY-12: Bottom chat bar for AI interaction
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';

interface ChatInterfaceProps {
  recentQueries: string[];
  onSendMessage: (message: string) => void;
  placeholder?: string;
}

export function ChatInterface({ 
  recentQueries, 
  onSendMessage, 
  placeholder = "Ask your companion anything..." 
}: ChatInterfaceProps) {
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQueryClick = (query: string) => {
    setMessage(query);
  };

  return (
    <div className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border-t border-gray-200/50 dark:border-zinc-700/50 p-4 shadow-lg">
      <div className="max-w-2xl mx-auto">
        {/* Recent Queries */}
        {recentQueries.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 dark:text-zinc-400 mb-2">Recent queries:</p>
            <div className="space-y-1">
              {recentQueries.slice(0, 3).map((query, index) => (
                <button
                  key={index}
                  onClick={() => handleQueryClick(query)}
                  className="block text-sm text-gray-600 dark:text-zinc-300 hover:text-[#5B6FED] dark:hover:text-blue-400 transition-colors"
                >
                  • {query}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="flex gap-2">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 min-h-[44px] max-h-[120px] resize-none rounded-full px-4 py-3"
            rows={1}
          />
          <Button
            onClick={handleSend}
            disabled={!message.trim()}
            className="rounded-full w-11 h-11 p-0 bg-[#5B6FED] hover:bg-[#4A5FDD]"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>

        <p className="text-xs text-gray-400 mt-2 text-center">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}

